import _ from 'lodash';
import {collapseSpaces} from '@packages/systems/core/utils/TextUtils';
import {
  splitCssValue,
  cssToPropertyList,
  type CssProperty,
} from '@packages/systems/core/utils/CssStringUtils';

import {beautifyCss} from './CssFormatUtils';
import {safeFamilyName} from '@packages/systems/core/utils/FontUtils';
import {
  hasSwatchPlaceholder,
  createSwatchMap,
  replaceSwatchPlaceholdersForCSSOM,
  replaceSwatchPlaceholdersForExport,
} from '@packages/systems/style-legacy/swatches';
import {grid, template} from 'grid-template-parser';

import type {
  MixinMap,
  SwatchType,
  AppliedMixinKey,
  MixinKey,
} from '@packages/systems/core/utils/StyleTypes';
import {
  type VariableMap,
  type VariableID,
  emptyVariableMap,
  hasVariablePlaceholder,
  replaceVariablePlaceholdersForCSSOM,
  replaceVariablePlaceholdersForPublish,
  VariableCollectionMap,
  getVariableIdFromDefinitionPlaceholder,
  renderCssVariableName,
} from '@packages/systems/core/utils/StyleVariables';
import type {Font} from '@packages/systems/core/constants/Fonts';
import {
  containsCssCustomValue,
  extractCssCustomValue,
} from '@packages/systems/style/raw-values';
import {Plugins} from '@packages/systems/wfdl';
import {emptyObject} from '@packages/utilities/fp/utils';
import {defer, force, Lazy} from '@packages/utilities/lazy';
import {cssPropDetailsFromNativeProperty} from '@packages/shared/LegacyStyleSystem/utils/CssPropertyUtils';

// TODO: Make sure the legacy BgImageParser.js uses this value
const IMAGE_FILE_PREFIX = '@img_';

const lazyEmptyObject = defer(() => emptyObject);

/**
 * Converts a LESS string into the final CSSOM-compatible CSS style string.
 * @param  {String}       styleLess           The styleLess text that is stored inside a StyleRule.
 * @param  {Boolean}      options.beautify    If true, will format CSS text like it would appear in the published CSS file.
 * @param  {Function}     options.getImageUrl   A function that will return an URL and accept image id extracted from @img_123 placeholders.
 * @param  {CssVariablesState}  options.assetState  The Flux CssVariablesState needed to convert @swatch_123 placeholders to baked colors.
 * @return {String}                           The final CSS style text string.
 */
export function compileLess(
  styleLess: string,
  {
    slugMap,
    getImageUrl,
    beautify = false,
    swatches,
    variables = emptyVariableMap,
    variableCollections,
    mixins = lazyEmptyObject,
    fonts,
    mut_seenVariables,
    mut_seenMixins,
  }: {
    slugMap: Plugins.PluginsType['slugMap'];
    getImageUrl?: (arg1: string) => string | null;
    swatches?: SwatchType[];
    variables?: VariableMap;
    variableCollections: VariableCollectionMap;
    mixins?: Lazy<MixinMap>;
    fonts?: Font[];
    beautify?: boolean;
    mut_seenVariables?: Set<VariableID>;
    mut_seenMixins?: Set<AppliedMixinKey>;
  }
): string {
  const lessProps = cssToPropertyList(styleLess);
  const patchedLessProps = patchGridTemplateAreas(lessProps);
  const swatchMap = swatches ? createSwatchMap(swatches) : {};
  /**
   * A mapping of `cssProps` index to the original `patchedLessProps` item
   * index. We only need this because it's possible that a property from
   * `patchedLessProps` is not part of `cssProps` due to omission.
   *
   * We use this map to look up original `@raw<|...|>` values from
   * `patchedLessProps` in the `hasRawValue` predicate function passed to
   * `beautifyCss`, below.
   */
  const indexLookupMap = new Map<number, number>();
  const cssProps: string[] = patchedLessProps.reduce<Array<any>>(
    (memo, prop, index) => {
      const cssProp = compileLessProperty(
        prop,
        swatches,
        swatchMap,
        variables,
        variableCollections,
        mixins,
        slugMap,
        fonts,
        getImageUrl,
        beautify,
        mut_seenVariables,
        mut_seenMixins
      );

      if (cssProp !== null) {
        indexLookupMap.set(memo.length, index);
        memo.push(cssProp);
      }

      return memo;
    },
    []
  );

  let cleanCss = cssProps.join(' ');

  if (beautify) {
    cleanCss = beautifyCss(
      cleanCss,
      // We override the default `hasRawValue` function here because the
      // `cleanCss` value no longer contains `@raw<|...|>` placeholders.
      //
      // We can look up the original property based on its index from the
      // original `cleanCss` string, which still contains `@raw<|...|>`
      // placeholders (if any).
      (compiledPropVal, index) => {
        const mappedIndex = indexLookupMap.get(index);
        if (mappedIndex == null) return false;
        const originalPropVal = patchedLessProps[mappedIndex];
        if (originalPropVal == null) return false;
        return (
          typeof originalPropVal.value === 'string' &&
          containsCssCustomValue(originalPropVal.value)
        );
      }
    );
  }

  return cleanCss;
}

function isMixinKey(key: string): key is MixinKey {
  return key.startsWith('---');
}

function compileLessProperty(
  // upstream type from cssToPropertyList is FixMeAny
  prop: any,
  // `swatches` is passed as well since `compileLessProperty` can call
  // `compileLess` when it encounters mixins
  swatches: SwatchType[] | undefined,
  swatchMap: {
    [key: string]: string;
  },
  variables: VariableMap,
  variableCollections: VariableCollectionMap,
  mixins: Lazy<MixinMap>,
  slugMap: Plugins.PluginsType['slugMap'],
  fonts: Font[] | undefined,
  getImageUrl: ((arg1: string) => string | null) | undefined,
  beautify: boolean,
  mut_seenVariables?: Set<VariableID>,
  mut_seenMixins?: Set<AppliedMixinKey>
): string | null {
  const {property: property_, value} = prop;
  let property = property_;
  if (!property_) {
    // if property name is missing, we want to drop this css prop-value pair
    return null;
  }

  const variableId = getVariableIdFromDefinitionPlaceholder(property);
  if (variableId != null) {
    property = `--${renderCssVariableName(variables, variableCollections, slugMap, variableId)}`;
  }

  const propDetails = cssPropDetailsFromNativeProperty(property);
  let finalProperty = property;
  let cleanValue = value;

  // Expand mixins as we encounter them:
  if (isMixinKey(property)) {
    const mixinValues = force(mixins)[property];
    // We simply keep `---`-prefixed variables if they don't correspond to a
    // known mixin. This allows us to use the `---` prefix for other utility
    // purposes, not just mixins.
    if (mixinValues != null) {
      const mixinStyleLess = mixinValues[value];
      if (mixinStyleLess != null) {
        if (mut_seenMixins != null) {
          mut_seenMixins.add(`${property}:${value}`);
        }
        return compileLess(force(mixinStyleLess), {
          slugMap,
          getImageUrl,
          beautify,
          swatches,
          variables,
          variableCollections,
          mixins,
          fonts,
          mut_seenVariables,
          mut_seenMixins,
        });
      }
    }
  }

  // In some cases (e.g. backface-visibility) we need to add a vendor prefix to the property to appease the current browser
  if (propDetails && propDetails.prefix) {
    finalProperty = propDetails.prefix + property;
  }

  // If a property allows swatch placeholders, replace them with either:
  // * CSS variable accessors in preview mode
  // * Baked CSS colors for export/beautify mode
  if (propDetails && propDetails.allowSwatches && hasSwatchPlaceholder(value)) {
    if (beautify) {
      cleanValue = replaceSwatchPlaceholdersForExport(
        cleanValue,
        swatchMap,
        variables,
        variableCollections,
        slugMap,
        mut_seenVariables
      );
    } else {
      cleanValue = replaceSwatchPlaceholdersForCSSOM(
        cleanValue,
        variables,
        variableCollections,
        slugMap
      );
    }
  }

  if (containsCssCustomValue(cleanValue)) {
    cleanValue = extractCssCustomValue(cleanValue, {beautify});
  }

  let propContainsVariables: boolean = false;
  // If a property allows variables, replace variable placeholders with
  // `var(...)` syntax
  if (propDetails && hasVariablePlaceholder(cleanValue)) {
    const previousCleanValue = cleanValue;
    if (beautify) {
      cleanValue = replaceVariablePlaceholdersForPublish(
        variables,
        variableCollections,
        slugMap,
        cleanValue,
        mut_seenVariables
      );
    } else {
      cleanValue = replaceVariablePlaceholdersForCSSOM(
        variables,
        variableCollections,
        slugMap,
        cleanValue
      );
    }
    if (cleanValue !== previousCleanValue) {
      propContainsVariables = true;
    }
  }

  // Remove comments
  if (typeof cleanValue === 'string') {
    cleanValue = _.trim(
      collapseSpaces(cleanValue.replace(/\s*\/\*[\s\S]*?\*\//g, ''))
    );
  }

  if (property === 'font-family') {
    cleanValue = propContainsVariables
      ? cleanValue
      : safeFamilyName(cleanValue, fonts);
  } else if (property === 'background-image') {
    if (cleanValue && getImageUrl) {
      const lessParts = splitCssValue(cleanValue, ',');
      const cssParts = lessParts.map(function (lessPart) {
        if (_.startsWith(lessPart, IMAGE_FILE_PREFIX)) {
          const imageGuid = lessPart.substr(IMAGE_FILE_PREFIX.length);
          const imageUrl = getImageUrl(imageGuid);
          return imageUrl ? 'url("' + imageUrl + '")' : 'none';
        }

        return lessPart;
      });

      cleanValue = cssParts.join(', ');
    }
  } else if (property === 'background-clip') {
    let result = `${property}: ${cleanValue}; -webkit-${property}: ${cleanValue};`;

    if (!beautify) {
      if (cleanValue === 'text') {
        result += ' -webkit-text-fill-color: transparent;';
      } else {
        // Explicit `inherit` is required to prevent accidentally inheriting `transparent` from media queries.
        result += ' -webkit-text-fill-color: inherit;';
      }
    }

    return result;
  } else if (property === 'backdrop-filter') {
    // Need `-webkit-` prefix for Safari support:
    return `${property}: ${cleanValue}; -webkit-${property}: ${cleanValue};`;
  }

  return finalProperty + ': ' + cleanValue + ';';
}

const GRID_TEMPLATE_AREAS = 'grid-template-areas';
const GRID_TEMPLATE_ROWS = 'grid-template-rows';

/**
 * Patches grid-template-areas in properties to match grid-template-rows
 *
 * We use Lightning CSS to optimize CSS stylesheets during publishing,
 * which includes collapsing grid-template-areas, grid-template-columns
 * and grid-template-rows CSS property values into a single grid-template
 * CSS property definition.
 *
 * When Lightning CSS encounters such a definition where the value of
 * grid-template-rows defines more rows than are described by the value
 * of grid-template-areas, it outputs a grid-template value that ignores
 * the "extra" rows defined by grid-template-rows. This causes a bug where
 * the WYSIWYG relationship between Designer and the Published Site breaks
 * down, because the Published Site stylesheet is missing rows that are
 * shown in Designer and Designer Preview.
 *
 * Related JIRA ticket: https://webflow.atlassian.net/browse/PLAT-2367
 *
 * Example of the bug
 * ------------------
 *
 * .css-in-designer {
 *   grid-template-areas:
 *     ". a"
 *     ". a";
 *   grid-template-rows: 100px auto 100px;
 *   grid-template-coluns: 50px;
 * }
 *
 * .css-in-published-site-before-this-patch {
 *   grid-template:
 *     ". a" 100px
 *     ". a" / 50px;
 * }
 *
 * This function patches grid-template-areas in properties so that the
 * height of the grid described matches the number of rows defined by
 * the grid-template-rows value, if the number of grid-template-rows is
 * greater than the grid-template-areas height. In this case, the
 * function returns a new shallow copy of properties with a patched
 * grid-template-areas.
 *
 * .css-in-published-site-after-this-patch {
 *   grid-template:
 *     ". a" 100px
 *     ". a"
 *     ". ." 100px / 50px;
 * }
 *
 * Otherwise, this function return the given properties.
 */
function patchGridTemplateAreas(properties: CssProperty[]) {
  let areasProp;
  let rowsProp;

  for (const prop of properties) {
    if (prop.property === GRID_TEMPLATE_AREAS) {
      areasProp = prop;
    }

    if (prop.property === GRID_TEMPLATE_ROWS) {
      rowsProp = prop;
    }
  }

  if (
    areasProp &&
    areasProp.value &&
    typeof areasProp.value === 'string' &&
    rowsProp &&
    rowsProp.value &&
    typeof rowsProp.value === 'string'
  ) {
    const areasPropValue: string = areasProp.value;
    const rowsPropValue: string = rowsProp.value;

    const areas = grid(areasPropValue);
    const rows = countGridTemplateRows(rowsPropValue);

    // check if the grid described by grid-template-areas has fewer rows
    // than are defined in grid-template-rows
    if (rows > 0 && areas.height > 0 && rows > areas.height) {
      // if grid-template-areas is "missing" rows, we patch the value so
      // that the height of the grid described by grid-template-areas
      // matches the number of rows defined in grid-template-rows
      const nextAreasProp = {
        property: GRID_TEMPLATE_AREAS,
        value: template({...areas, height: rows}),
      } as const;

      return properties.map((prop) =>
        prop === areasProp ? nextAreasProp : prop
      );
    }
  }

  return properties;
}

// count the number of rows defined in a grid-template-rows CSS property value
function countGridTemplateRows(gridTemplateRowsValue: string): number {
  // normalize whitespace in the value so that we can trust counting spaces
  const normalizedValue = gridTemplateRowsValue.replace(/\s+/g, ' ');

  const length = normalizedValue.length;

  if (length === 0) {
    // no value, no rows
    return 0;
  }

  // the grid-template-rows values produced by our CSS Grid implementation
  // consist of values for each row separated by spaces
  // we assume the values being parsed were produced by our style system
  let spaceCount = 0;
  // the grid-template-rows value can define a row using CSS functions
  // like minmax(...) that can contain spaces to separate arguments between
  // the paretheses
  let openParenthesesCount = 0;

  for (let i = 0; i < length; i++) {
    const char = normalizedValue[i];

    if (char === '(') {
      openParenthesesCount += 1;
    }

    if (char === ')') {
      openParenthesesCount -= 1;
    }

    // only count spaces when there are no open paretheses to ensure
    // we don't include spaces inside CSS function calls in the count
    if (char === ' ' && openParenthesesCount === 0) {
      spaceCount += 1;
    }
  }

  // if we find n spaces, there should be n+1 values separated by those spaces
  // we assume that the value has been trimmed by compileLessProperty
  return spaceCount + 1;
}
